import React, { useMemo, useState } from 'react';
import { Crown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../../contexts/SettingsContext';
import { useAuth } from '../../contexts/AuthContext';
import { CHAT_SOUND_PRESETS, PLUS_CHAT_SOUND_PRESETS, playChatSound, DEFAULT_CHAT_SOUND_ID } from '../../utils/chatSoundPresets';
import SettingsToggle from './SettingsToggle';

const ChatSettingsSection = () => {
  const { t } = useTranslation('settings');
  const { preferences, updatePreferences, setChatNotificationSoundId, setGroupNotificationSoundId } = useSettings();
  const { isPlusUser } = useAuth();
  const [playingSoundId, setPlayingSoundId] = useState(null);

  const selectedChatSoundId = useMemo(() => preferences.chatNotificationSoundId || DEFAULT_CHAT_SOUND_ID, [preferences.chatNotificationSoundId]);
  const selectedGroupSoundId = useMemo(() => preferences.groupNotificationSoundId || DEFAULT_CHAT_SOUND_ID, [preferences.groupNotificationSoundId]);

  const handleChatSoundChange = (soundId) => setChatNotificationSoundId(soundId);
  const handleGroupSoundChange = (soundId) => setGroupNotificationSoundId(soundId);

  const handlePreview = async (soundId) => {
    setPlayingSoundId(soundId);
    try {
      await playChatSound(soundId);
    } catch (e) {
      console.error('Chat ses önizlemesi çalınamadı:', e);
    } finally {
      setPlayingSoundId((c) => (c === soundId ? null : c));
    }
  };

  const FREE_CHAT_SOUND_LIMIT = 2;
  const allPresets = [...CHAT_SOUND_PRESETS, ...PLUS_CHAT_SOUND_PRESETS];
  const muted = preferences.chatNotificationsMuted === true;

  return (
    <>
      <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-8">
        {t('chatSection.title')}
      </h1>

      <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-200 uppercase tracking-wider mb-4 mt-8">
        {t('chatSection.notifications')}
      </h2>
      <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between p-4 bg-white dark:bg-[var(--color-bg-base)] border-b border-slate-100 dark:border-slate-800/50 last:border-b-0">
          <div>
            <p className="font-medium text-slate-900 dark:text-slate-200">{t('chatSection.muteLabel')}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t('chatSection.muteDescription')}</p>
          </div>
          <SettingsToggle
            checked={muted}
            onChange={() => updatePreferences({ chatNotificationsMuted: !muted })}
            aria-label={t('chatSection.muteLabel')}
          />
        </div>
      </div>

      <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-200 uppercase tracking-wider mb-4 mt-8">
        {t('chatSection.notificationSound')}
      </h2>
      <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
        {allPresets.map((preset, index) => {
          const freeIndex = CHAT_SOUND_PRESETS.indexOf(preset);
          const isLocked = !isPlusUser && (preset.isPlus || freeIndex === -1 || freeIndex >= FREE_CHAT_SOUND_LIMIT);
          const isChatSelected = selectedChatSoundId === preset.id;
          const isGroupSelected = selectedGroupSoundId === preset.id;
          const isPlaying = playingSoundId === preset.id;
          const isLast = index === allPresets.length - 1;
          return (
            <div
              key={preset.id}
              className={`flex items-center justify-between p-4 bg-white dark:bg-[var(--color-bg-base)] border-b border-slate-100 dark:border-slate-800/50 ${isLast ? 'border-b-0' : ''} ${isLocked ? 'opacity-60' : ''}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                {isLocked && <Crown className="w-4 h-4 text-amber-500 dark:text-amber-400 flex-shrink-0" />}
                <p className="font-medium text-slate-900 dark:text-slate-200 truncate">{preset.name}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                <button
                  type="button"
                  onClick={() => handlePreview(preset.id)}
                  disabled={muted || isPlaying || isLocked}
                  className="px-3 py-1.5 text-xs font-medium rounded-md border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-[var(--color-bg-muted)] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isPlaying ? t('chatSection.playing') : t('chatSection.listen')}
                </button>
                <button
                  type="button"
                  onClick={() => handleChatSoundChange(preset.id)}
                  disabled={isChatSelected || isLocked}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${isChatSelected ? 'bg-blue-600 text-white' : 'border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-[var(--color-bg-muted)]'}`}
                >
                  {t('chatSection.oneOnOne')}
                </button>
                <button
                  type="button"
                  onClick={() => handleGroupSoundChange(preset.id)}
                  disabled={isGroupSelected || isLocked}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${isGroupSelected ? 'bg-purple-600 text-white' : 'border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-[var(--color-bg-muted)]'}`}
                >
                  {t('chatSection.group')}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {!isPlusUser && (
        <div className="flex items-start gap-3 mt-4 p-4 rounded-xl border border-amber-500/30 bg-amber-500/5 dark:bg-amber-500/10">
          <Crown className="w-5 h-5 text-amber-500 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-700 dark:text-amber-400">{t('chatSection.plusSoundPackTitle')}</p>
            <p className="text-xs text-amber-600/90 dark:text-amber-400/90 mt-1">{t('chatSection.plusSoundPackDescription')}</p>
          </div>
        </div>
      )}

      {muted && (
        <p className="mt-4 text-sm text-amber-600 dark:text-amber-400">
          {t('chatSection.mutedHint')}
        </p>
      )}

      <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
        {t('chatSection.footerHint')}
      </p>
    </>
  );
};

export default ChatSettingsSection;
